import { Card, CardContent } from "@mui/material";
import { memo, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import ExpandableCardContent from "@/components/common/cards/expandableCard/expandableCardContent";
import ExpandableCardThumbnail from "@/components/common/cards/expandableCard/expandableCardThumbnail";
import { GREY } from "@/config/mui/colorPalette";

type ExpandableCardProps = {
  height: number;
  isFeaturedDeal?: boolean;
  thumbnail: (thumbnailHeight: number) => ReactNode;
  content: (isFocused: boolean) => ReactNode;
  button: (isFocused: boolean) => ReactNode;
  isMobile: boolean;
};

const MemoizedExpandableCardThumbnail = memo(ExpandableCardThumbnail);
const MemoizedExpandableCardContent = memo(ExpandableCardContent);

export default function ExpandableCard({
  height,
  isFeaturedDeal,
  thumbnail,
  content,
  button,
  isMobile,
}: ExpandableCardProps) {
  const [isFocused, setIsFocused] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const { regularHeight, collapsedHeight, thumbnailHeight, expansionHeight } =
    useMemo(() => {
      const regularHeight = 0.8 * height;
      const collapsedHeight = 0.6 * height;
      const thumbnailHeight = 0.4 * height;
      const expansionHeight = regularHeight - collapsedHeight;

      return {
        regularHeight,
        collapsedHeight,
        thumbnailHeight,
        expansionHeight,
      };
    }, [height]);

  // This effect is needed for featured deals section, as cards stay focused when they are not in view which causes the section to have invalid height
  useEffect(() => {
    if (!isFeaturedDeal) return;

    const currentCard = cardRef.current;

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) {
        setIsFocused(false);
      }
    });

    if (currentCard) {
      observer.observe(currentCard);
    }

    return () => {
      if (currentCard) {
        observer.unobserve(currentCard);
      }
    };
  }, [isFeaturedDeal]);

  return (
    <Card
      ref={cardRef}
      sx={{
        width: "auto",
        height: isMobile ? "auto" : height,
        border: `1px solid ${GREY[20]}`,
        borderRadius: "16px !important",
      }}
      {...(isMobile
        ? {
            onClick: () => setIsFocused(!isFocused),
          }
        : {
            onMouseEnter: () => setIsFocused(true),
            onMouseLeave: () => setIsFocused(false),
          })}
    >
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: "0 !important",
          position: "relative",
        }}
      >
        <MemoizedExpandableCardThumbnail
          thumbnail={thumbnail}
          thumbnailHeight={thumbnailHeight}
          expansionHeight={expansionHeight}
          isFocused={isFocused}
        />

        <MemoizedExpandableCardContent
          regularHeight={regularHeight}
          collapsedHeight={collapsedHeight}
          content={content}
          button={button}
          isFocused={isFocused}
          isMobile={isMobile}
        />
      </CardContent>
    </Card>
  );
}
